/**
 * Transforms hierarchical API response from aggrMetricsAcrossDimensions
 * into the { nodes, links } format required by recharts Sankey.
 *
 * API shape:  [{ display, count, children: [{ display, count, children: [...] }] }]
 * Sankey shape: { nodes: [{ name, level }], links: [{ source, target, value }] }
 *
 * Nodes at different hierarchy levels are separate even if they share the same display name
 * (e.g. "Other" at level 0 vs "Other" at level 1). Nodes at the SAME level with the same
 * display name are merged into a single node (shared target).
 */

const normalizeDisplay = (display) => {
  if (Array.isArray(display)) return display[0] || "(empty)";
  return display || "(empty)";
};

export const transformHierarchyToSankey = (hierarchicalData) => {
  if (!hierarchicalData?.length) return { nodes: [], links: [] };

  const nodes = [];       // [{ name, level }]
  const links = [];       // [{ source, target, value }]
  const nodeIndexMap = {}; // "level:displayName" → index in nodes[]

  const getOrCreateNode = (display, level) => {
    const key = `${level}:${display}`;
    if (nodeIndexMap[key] !== undefined) return nodeIndexMap[key];

    const idx = nodes.length;
    nodes.push({ name: display, level });
    nodeIndexMap[key] = idx;
    return idx;
  };

  const walkLevel = (items, level, parentIdx) => {
    for (const item of items) {
      const display = normalizeDisplay(item.display);

      // Skip partial-dimension sentinel nodes (value set to "(N/A)" by backend)
      const rawValue = Array.isArray(item.value) ? item.value[0] : item.value;
      if (rawValue === "(N/A)") continue;

      if (parentIdx === null) {
        // Top-level: just create nodes at level 0
        const nodeIdx = getOrCreateNode(display, level);

        if (item.children?.length) {
          walkLevel(item.children, level + 1, nodeIdx);
        }
      } else {
        // Child level: create node + link from parent
        const nodeIdx = getOrCreateNode(display, level);
        const count = item.count || 0;

        if (count > 0) {
          // Check if a link already exists between these two nodes (possible when
          // same child appears under same parent from normalized data)
          const existingLink = links.find(
            (l) => l.source === parentIdx && l.target === nodeIdx
          );
          if (existingLink) {
            existingLink.value += count;
          } else {
            links.push({ source: parentIdx, target: nodeIdx, value: count });
          }
        }

        if (item.children?.length) {
          walkLevel(item.children, level + 1, nodeIdx);
        }
      }
    }
  };

  walkLevel(hierarchicalData, 0, null);

  // Filter out any zero-value links (shouldn't happen but safety net)
  const filteredLinks = links.filter((l) => l.value > 0);

  return { nodes, links: filteredLinks };
};
